// Centrifugal pump vertex schema
// Adds energy to fluid via rotating impeller

import { z } from 'zod';
import { BaseVertexSchema } from '../base/vertex';
import { ValueWithUnitSchema } from '../base/common';
import { PumpCurveSchema, PumpCurvePointSchema } from '../base/curves';
import {
  ActiveDevicePressureDifferentialConfigSchema,
  ActiveDevicePressureSuctionConfigSchema,
  ActiveDevicePressureDischargeConfigSchema,
} from '../base/active-device-operation';

/**
 * Pump Operation Mode Configurations
 * ADR-0006: Operating Mode Configuration Pattern
 * 
 * Different modes require different sets of properties.
 * Using nested config to make invalid states unrepresentable.
 * 
 * Note: Flat structure preferred - properties are at the config level,
 * not wrapped in an additional 'value' object.
 */

// Modes: Pressure (differential rise, or a held suction / discharge pressure).
//
// Reused from the shared active-device schemas rather than redeclared — they
// carry `pressure_type` ("static" = PIPE-FLO's "P Static @ suction|discharge"),
// and a local copy that omitted it made Zod strip the flag off every imported
// pump, silently demoting a static setpoint to a total one.

// Mode: Flow (fixed flow rate)
const PumpFlowVolumetricConfigSchema = z.object({
  mode: z.literal('flow_volumetric'),
  value: z.number(),
  unit: z.string(), // e.g., "m³/s"
});

const PumpFlowMassConfigSchema = z.object({
  mode: z.literal('flow_mass'),
  value: z.number(),
  unit: z.string(), // e.g., "kg/s"
});

// Mode: Head (fixed head rate)
const PumpHeadModeConfigSchema = z.object({
  mode: z.literal('head'),
  value: z.number(),
  unit: z.string(), // e.g., "m", "ft"
});

// Mode: Speed (specified pump speed)
const PumpSpeedModeConfigSchema = z.object({
  mode: z.literal('specified_speed'),
  value: z.number().positive(), // Pump speed in rpm
  unit: z.string(), // e.g., "rpm"
});

// Discriminated union of pump operation modes
export const PumpOperationConfigSchema = z.discriminatedUnion('mode', [
  ActiveDevicePressureDifferentialConfigSchema,
  ActiveDevicePressureSuctionConfigSchema,
  ActiveDevicePressureDischargeConfigSchema,
  PumpFlowVolumetricConfigSchema,
  PumpFlowMassConfigSchema,
  PumpHeadModeConfigSchema,
  PumpSpeedModeConfigSchema
]);

export type PumpOperationConfig = z.infer<typeof PumpOperationConfigSchema>;

/**
 * Pump Curve Point Schema
 * A single data point on the pump performance curve.
 * Flow values must be unique across the array (enforced by PumpCurveGridDialog).
 *
 * Single source of truth relocated to ../base/curves (Task 5 Step 3b) so the
 * curve schema can add `power?` without a circular import / shadowing.
 * Direction: vertices/ → base/ (matches compressor.ts/fan.ts).
 */
export { PumpCurvePointSchema, type PumpCurvePoint } from '../base/curves';

/**
 * Centrifugal Pump Vertex Schema
 * Adds energy to the fluid via a rotating impeller
 * 
 * Uses nested config pattern (ADR-0006) because different modes require
 * different sets of properties (pressure mode vs flow mode)
 * 
 * Note: Centrifugal pumps do NOT have a single elevation field. Instead, they use
 * suction_elevation and discharge_elevation to represent the two connection points
 * at different heights, which is essential for calculating pump head correctly.
 * 
 * @property suction_elevation - Height of the suction connection point
 * @property discharge_elevation - Height of the discharge connection point
 */
export const CentrifugalPumpVertexSchema = BaseVertexSchema.extend({
  type: z.literal('centrifugal_pump'),

  // Calculation data
  suction_elevation: ValueWithUnitSchema,
  discharge_elevation: ValueWithUnitSchema,
  // Accepts both the rich PumpCurve shape (catalog imports + new manual saves)
  // and the legacy flat-array shape (older hand-entered curves stored before
  // PumpCurveGridDialog was upgraded). The calc engine's parsePumpCurve returns
  // null for the flat-array shape, so legacy models continue to produce no pump
  // contribution until the user reopens the curve editor and the dialog
  // upgrades the shape on save. Intentional — we don't silently re-coerce.
  pump_curve: z.union([
    PumpCurveSchema,
    z.array(PumpCurvePointSchema).min(1),
  ]).optional(),
  operation: PumpOperationConfigSchema,

  // Operating impeller diameter for affinity-law scaling. When unset, the
  // calc engine applies diameter ratio = 1 (curve is taken as-is at its
  // reference diameter). When set, ratio = operating_diameter / test_diameter.
  // Surfaced in PumpForm only when a reference diameter is resolvable on
  // pump_curve (top-level test_diameter, raw_curves[0].diameter_value, or
  // pump_flo_selection.diameter_mm).
  operating_diameter: ValueWithUnitSchema.optional(),

  // v2 additions
  preferred_operating_range: z.object({
    from: z.number(),
    to: z.number(),
  }).optional(),
  npsh_margin: ValueWithUnitSchema.optional(),
});

export type CentrifugalPumpVertex = z.infer<typeof CentrifugalPumpVertexSchema>;

